/**
 * ヤギオファーチャットへのログイン後リダイレクト用ヘルパー。
 *
 * OAuth（Google/Apple 等）はブラウザの実遷移を伴うリダイレクト認証のため、
 * 認証完了後に元のURL（クエリパラメータ込み）へ確実に戻ってくる保証がない。
 * そこで、リダイレクト認証を開始する前に `offerChatRedirectUrl` を
 * sessionStorage へ退避しておき、認証完了後の読み出し時は
 * URLクエリ → sessionStorage の順にフォールバックして取得する。
 */

const STORAGE_KEY = 'offerChatRedirectUrl'

function readFromQuery(): string | null {
  return new URLSearchParams(window.location.search).get('offerChatRedirectUrl')
}

/**
 * OAuthのリダイレクト認証（signInWithRedirect 等）を開始する直前に呼び、
 * 現在のURLに `offerChatRedirectUrl` があれば sessionStorage へ退避する。
 */
export function stashOfferChatRedirectUrl(): void {
  const value = readFromQuery()
  if (value) {
    window.sessionStorage.setItem(STORAGE_KEY, value)
  }
}

/**
 * `offerChatRedirectUrl` を取得する。URLクエリに無ければ sessionStorage から読み、
 * 読み出したエントリは使い切りとして削除する。
 */
export function consumeOfferChatRedirectUrl(): string | null {
  const fromQuery = readFromQuery()
  if (fromQuery) {
    return fromQuery
  }
  const fromStorage = window.sessionStorage.getItem(STORAGE_KEY)
  if (fromStorage) {
    window.sessionStorage.removeItem(STORAGE_KEY)
  }
  return fromStorage
}

/**
 * sessionStorage に `offerChatRedirectUrl` が退避されているかだけを確認する
 * （消費はしない）。LINEログインのように `/login` を経由せず別ページへ直接
 * 着地する認証フローで、ページ遷移前に判定するために使う。
 */
export function hasStashedOfferChatRedirectUrl(): boolean {
  return !!window.sessionStorage.getItem(STORAGE_KEY)
}

/**
 * ログイン済みユーザーの customToken を取得し、ヤギオファーチャットへリダイレクトする。
 * NewLogin.vue / Login.vue の OAuth 復帰後だけでなく、router.js の LINE custom_token
 * ガードからも共通で呼べるよう Vue コンポーネントに依存しない形にしている。
 *
 * @param axios - `this.$axios` 相当の axios インスタンス
 * @param endpoint - `this.$varuna.endPoints.getYagiOfferChatToken` 相当のエンドポイントURL
 * @param uid - Firebase の user.uid
 * @param offerChatRedirectUrl - "/offers"・"/offers/{offerId}" のようなパス、
 *   または "https://..." のような完全URL（この場合はベースURLを付与しない）
 * @param fallbackUrl - 失敗時の遷移先（既定は "/mypage"）
 */
export async function redirectToYagiOfferChat(
  axios: {
    post: (
      url: string,
      data: FormData
    ) => Promise<{ data: { custom_token: string } }>
  },
  endpoint: string,
  uid: string,
  offerChatRedirectUrl: string,
  fallbackUrl = '/mypage'
): Promise<void> {
  try {
    const formData = new FormData()
    formData.append('uuid', uid)
    const res = await axios.post(endpoint, formData)
    const customToken = res.data.custom_token
    // offerChatRedirectUrl が既に完全URL（http/httpsで始まる）の場合は
    // ベースURLを重ねて連結しない（二重連結によるリダイレクト先破損を防止）
    const isAbsoluteUrl = /^https?:\/\//.test(offerChatRedirectUrl)
    const yagiofferChatBaseUrl = process.env.VUE_APP_YAGIOFFER_CHAT_URL || ''
    const baseUrl = isAbsoluteUrl
      ? offerChatRedirectUrl
      : yagiofferChatBaseUrl + offerChatRedirectUrl
    const targetUrl =
      baseUrl + '?customToken=' + encodeURIComponent(customToken)
    window.location.href = targetUrl
  } catch (e) {
    console.error(
      '[offerChatRedirect] ヤギオファーチャットへのリダイレクト失敗:',
      e
    )
    window.location.href = fallbackUrl
  }
}
